import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { errorText, ok, useApi } from '../lib/api';
import { when } from '../lib/datetime';
import { useDraft } from '../lib/draft';
import { RedemptionsTable } from './RedemptionsTable';

const tab =
  'rounded border-2 border-ink px-3 py-1 text-sm font-semibold hover:bg-caption/50 aria-selected:bg-ink aria-selected:text-newsprint';

/**
 * The player detail's scan triggers (ADR 0029): their redemptions (with the stored result and void), and the cosmetics
 * trigger redemptions granted them (account-level, kept across runs; a void redemption doesn't take one back, so it
 * still shows). Pass cosmetics come from the supporter pass (see Purchases).
 */
export function PlayerTriggerSection({ playerId }: { playerId: string }) {
  const [view, setView] = useState<'redemptions' | 'cosmetics'>('redemptions');
  return (
    <section className="rounded border-2 border-ink bg-white p-4 lg:col-span-2">
      <div className="mb-2 flex items-center gap-2" role="tablist">
        <h2 className="mr-2 font-semibold">Scan triggers</h2>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'redemptions'}
          className={tab}
          onClick={() => setView('redemptions')}
        >
          Redemptions
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'cosmetics'}
          className={tab}
          onClick={() => setView('cosmetics')}
        >
          Cosmetics
        </button>
      </div>
      {view === 'redemptions' ? (
        <RedemptionsTable filters={{ playerId }} showPlayer={false} take={10} />
      ) : (
        <PlayerCosmetics playerId={playerId} />
      )}
    </section>
  );
}

function PlayerCosmetics({ playerId }: { playerId: string }) {
  const { client } = useApi();
  const draft = useDraft();
  const names = new Map(
    (draft?.bundle.cosmetics ?? []).flatMap((c) =>
      c?.id ? [[c.id, c.name ?? c.id] as const] : [],
    ),
  );
  const grants = useQuery({
    queryKey: ['admin-trigger-redemptions', { playerId, cosmetics: true }],
    queryFn: async () =>
      ok(
        await client.GET('/admin/trigger-redemptions', {
          params: { query: { playerId, take: 200 } },
        }),
      ),
  });
  if (grants.isPending) return <p>Loading&hellip;</p>;
  if (grants.isError)
    return (
      <p className="text-blam" role="alert">
        {errorText(grants.error)}
      </p>
    );
  const owned = (grants.data.items ?? []).flatMap((r) =>
    (r.result?.grants?.cosmetics ?? []).map((id) => ({
      id,
      at: r.createdAt,
      trigger: r.triggerTitle || r.triggerId,
      status: r.status,
    })),
  );
  if (owned.length === 0)
    return <p className="text-sm text-bruise">No cosmetics from scan triggers.</p>;
  return (
    <ul className="text-sm">
      {owned.map((c) => (
        <li key={`${c.id}-${c.at}`} className="flex gap-2">
          <span className="font-semibold">{names.get(c.id) ?? c.id}</span>
          <span className="font-mono text-xs text-bruise">{c.id}</span>
          <span className="text-xs">
            from {c.trigger}, {when(c.at)}
            {c.status === 'void' ? ' (redemption void; still owned)' : ''}
          </span>
        </li>
      ))}
    </ul>
  );
}
